import { useState, useEffect } from 'react';

interface AvailabilityBlockEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (block: any) => Promise<void>;
  staff: any[];
  preselectedStaffId?: string;
  preselectedDate?: Date;
  editBlock?: any;
}

const AVAILABILITY_TYPES = [
  { value: 'unavailable', label: 'Unavailable', color: '#e2e8f0' },
  { value: 'break', label: 'Break', color: '#fef3c7' },
  { value: 'holiday', label: 'Holiday', color: '#e0f2fe' },
  { value: 'external', label: 'External', color: '#ede9fe' },
] as const;

const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTimeForInput = (date: Date): string => {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export default function AvailabilityBlockEditor({
  isOpen,
  onClose,
  onSave,
  staff,
  preselectedStaffId,
  preselectedDate,
  editBlock,
}: AvailabilityBlockEditorProps) {
  const [staffId, setStaffId] = useState('');
  const [type, setType] = useState<'unavailable' | 'break' | 'holiday' | 'external'>('unavailable');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editBlock) {
        setStaffId(editBlock.staffId || '');
        setType(editBlock.type || 'unavailable');
        setDate(formatDateForInput(editBlock.start));
        setStartTime(formatTimeForInput(editBlock.start));
        setEndTime(formatTimeForInput(editBlock.end));
        setTitle(editBlock.title || '');
      } else {
        setStaffId(preselectedStaffId || (staff.length > 0 ? staff[0].id : ''));
        setType('unavailable');
        setDate(preselectedDate ? formatDateForInput(preselectedDate) : formatDateForInput(new Date()));
        setStartTime('09:00');
        setEndTime('17:00');
        setTitle('');
      }
    }
  }, [isOpen, editBlock, preselectedStaffId, preselectedDate, staff]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!staffId || !date || !startTime || !endTime) {
      return;
    }

    setIsLoading(true);

    try {
      const [year, month, day] = date.split('-').map(Number);
      const [startHour, startMinute] = startTime.split(':').map(Number);
      const [endHour, endMinute] = endTime.split(':').map(Number);

      const startDate = new Date(year, month - 1, day, startHour, startMinute);
      const endDate = new Date(year, month - 1, day, endHour, endMinute);

      const selectedType = AVAILABILITY_TYPES.find(t => t.value === type);
      const selectedStaff = staff.find(s => s.id === staffId);

      const block = {
        id: editBlock?.id,
        staffId,
        staffName: selectedStaff?.name || '',
        start: startDate,
        end: endDate,
        type,
        title: title || undefined,
        backgroundColor: selectedType?.color,
        display: 'background' as const,
      };

      await onSave(block);
      onClose();
    } catch (error) {
      console.error('Failed to save availability block:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {editBlock ? 'Edit Availability Block' : 'Add Availability Block'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="staff" className="block text-sm font-medium text-gray-700 mb-1">
                Staff Member
              </label>
              <select
                id="staff"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select staff member</option>
                {staff.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type
              </label>
              <div className="grid grid-cols-2 gap-2">
                {AVAILABILITY_TYPES.map((availType) => (
                  <label
                    key={availType.value}
                    className={`flex items-center justify-center px-4 py-2 border rounded-md cursor-pointer transition-colors ${
                      type === availType.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="type"
                      value={availType.value}
                      checked={type === availType.value}
                      onChange={(e) => setType(e.target.value as typeof type)}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: availType.color }}
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {availType.label}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  id="startTime"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  id="endTime"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title / Reason (Optional)
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Lunch break, Annual leave"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
